import {
	type Client,
	ErrorCode,
	type JsonValue,
	OpenFeature,
	type OpenFeatureEventEmitter,
	type Provider,
	type ProviderStatus,
	type ResolutionDetails,
	StandardResolutionReasons,
} from '@openfeature/web-sdk';
import type { ValueOf } from '@/types';
import { type FeatureFlag as FeatureFlagName } from '@/constants/featureFlags';
import { type FeatureFlag } from '@/generated/graphql';
import { inject, type Ref } from 'vue';

type FlagType = 'boolean' | 'string' | 'number' | 'object';
export const FEATURE_FLAGS_PROVIDE_KEY = Symbol('feature-flags');
type FeatureFlagsProvideValue = Ref<FeatureFlag[]>;

let featureFlagsClient: Client | undefined;

class FeatureFlagsProvider implements Provider {
	public readonly runsOn = 'client';

	public status?: ProviderStatus;

	public events?: OpenFeatureEventEmitter;

	readonly metadata = {
		name: 'Feature Flags Provider',
	} as const;

	constructor(
		private readonly featureFlags: Ref<FeatureFlag[]> | undefined
	) {}

	getFeatureFlags(): FeatureFlag[] {
		return this.featureFlags?.value ?? [];
	}

	resolveBooleanEvaluation(
		flagKey: ValueOf<typeof FeatureFlagName>,
		defaultValue: boolean
	): ResolutionDetails<boolean> {
		return this.resolveValue(flagKey, 'boolean', defaultValue);
	}

	resolveStringEvaluation(
		flagKey: ValueOf<typeof FeatureFlagName>,
		defaultValue: string
	): ResolutionDetails<string> {
		return this.resolveValue(flagKey, 'string', defaultValue);
	}

	resolveNumberEvaluation(
		flagKey: ValueOf<typeof FeatureFlagName>,
		defaultValue: number
	): ResolutionDetails<number> {
		return this.resolveValue(flagKey, 'number', defaultValue);
	}

	resolveObjectEvaluation<T extends JsonValue>(
		flagKey: ValueOf<typeof FeatureFlagName>,
		defaultValue: T
	): ResolutionDetails<T> {
		return this.resolveValue(flagKey, 'object', defaultValue);
	}

	resolveValue<T>(
		flagKey: ValueOf<typeof FeatureFlagName>,
		flagValueType: FlagType,
		defaultValue: T
	): ResolutionDetails<T> {
		const flagValue = this.getFlagValue(flagKey, defaultValue);

		switch (flagValueType) {
			case 'boolean':
				if (!this.isBoolean(flagValue)) {
					return this.wrongTypeResult(defaultValue);
				}
				break;
			case 'string':
				if (!this.isString(flagValue)) {
					return this.wrongTypeResult(defaultValue);
				}
				break;
			case 'number':
				if (!this.isNumber(flagValue)) {
					return this.wrongTypeResult(defaultValue);
				}
				break;
			case 'object':
				if (!this.isObject(flagValue)) {
					return this.wrongTypeResult(defaultValue);
				}
				break;
		}

		return this.translateResult(flagValue, defaultValue);
	}

	getFlagValue<T>(flagKey: string, defaultValue: T): T {
		const flag = this.getFeatureFlags().find(
			(flag) => flag.name === flagKey
		);

		return flag ? JSON.parse(flag.value) : defaultValue;
	}

	wrongTypeResult<T>(defaultValue: T): ResolutionDetails<T> {
		return {
			value: defaultValue,
			reason: StandardResolutionReasons.ERROR,
			errorCode: ErrorCode.TYPE_MISMATCH,
		};
	}

	translateResult<T>(value: T, defaultValue: T): ResolutionDetails<T> {
		if (!value) {
			return {
				value: defaultValue,
				reason: StandardResolutionReasons.DEFAULT,
			};
		}

		return {
			value,
			reason: StandardResolutionReasons.TARGETING_MATCH,
		};
	}

	isBoolean(value: unknown): boolean {
		return typeof value === 'boolean';
	}

	isString(value: unknown): boolean {
		return typeof value === 'string';
	}

	isNumber(value: unknown): boolean {
		return typeof value === 'number';
	}

	isObject(value: unknown): boolean {
		return typeof value === 'object' && value !== null;
	}
}

export const clearFeatureFlagsClient = () => {
	featureFlagsClient = undefined;
};

export const useFeatureFlags = () => {
	if (featureFlagsClient === undefined) {
		const featureFlags = inject<FeatureFlagsProvideValue>(
			FEATURE_FLAGS_PROVIDE_KEY
		);

		OpenFeature.setProvider(new FeatureFlagsProvider(featureFlags));
		featureFlagsClient = OpenFeature.getClient();
	}

	const isFeatureEnabled = (feature: ValueOf<typeof FeatureFlagName>) => {
		return featureFlagsClient?.getBooleanValue(feature, false) ?? false;
	};

	return { featureFlagsClient, isFeatureEnabled };
};
